<template>
  <main-layout
    customControls
    controlsBg="light"
    :hasControlsBar="false"
    :breadcrumb="getBreadcrumb">
    <div class="container">
      <grid-row class="margin-top-xl" column-align="center">
        <grid-col col-size="4">
          <div id="header">
            <h1 class="header-title">
              <chevron
                @click="$router.push({name: 'Workspace'})"/>
              {{ $t('menu.licenseManagement') }}
            </h1>
            <div class="header-add-button">
              <router-link to="/addLicense">
                  <span
                    v-tooltip.left.top-start="$t('health.license.new')">
                    <font-awesome-icon
                      class="header-add-button-icon"
                      :icon="icon.add"/>
                  </span>
              </router-link>
            </div>
          </div>
          <alert
            v-if="alertVisible"
            :message="alertMessage"
            :type="alertType">
          </alert>
        </grid-col>
      </grid-row>
      <grid-row class="margin-top-xl" column-align="center">
        <grid-col col-size="4">
          <table-component
            ref="licenseTable"
            :data="fetchData"
            :show-caption="false"
            class="datatable"
            :show-filter="true">
            <table-column show="id" label="Id" :hidden="true"></table-column>
            <table-column
              show="email"
              :label="$t('user.email')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              show="license"
              :label="$t('health.userProfile.license')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              show="orderId"
              :label="$t('health.license.order')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              show="product.description"
              :label="$t('health.license.description')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              show="product.productId"
              :label="$t('health.license.productId')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              show="product.entitlement"
              :label="$t('health.license.entitlement')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              show="product.duration"
              :label="$t('health.license.duration')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              show="timestamp"
              :formatter="timeFormatter"
              :label="$t('health.license.date')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              label=""
              :sortable="false"
              :filterable="false"
              header-class="table-component__minimum__width__column"
              cell-class="text-center">
              <template slot-scope="row">
                <font-awesome-icon
                  v-on:click="onDelete(row)"
                  :icon="icon.trash"
                  class="table-iconset-icon"/>
              </template>
            </table-column>
          </table-component>
        </grid-col>
      </grid-row>
    </div>
    <ok-cancel-modal
      v-if="showConfirmationDeleteModal"
      v-on:ok="onDeleteConfirm"
      v-on:cancel="onDeleteCancel"
      :title="deleteModalMessage"
      :message="deleteModalMessage"
      :okButton="$t('commons.delete')"
      :cancelButton="$t('commons.cancel')">
    </ok-cancel-modal>
  </main-layout>
</template>

<script>
import MainLayout from '@/layouts//MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Chevron from '@/components/shared/Chevron';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableRow from '@/components/shared/table/components/TableRow';
import TableColumn from '@/components/shared/table/components/TableColumn';
import pagination from '@/components/shared/table/pagination';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import licenseApi from 'BRAND/api/license';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import Alert from '@/components/shared/alert/Alert';

export default {
  name: 'LicenseManagement',
  components: {
    MainLayout,
    Chevron,
    GridRow,
    GridCol,
    TableComponent,
    TableRow,
    TableColumn,
    FontAwesomeIcon,
    OkCancelModal,
    Alert
  },
  data () {
    return {
      licenses: [],
      licenseToDelete: {},
      showConfirmationDeleteModal: false,
      deleteModalMessage: '',
      icon: {
        add: faPlusCircle,
        trash: faTrashAlt
      },
      tablePageSize: 15,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      alertVisible: false,
      alertMessage: '',
      alertType: ''
    };
  },
  mounted () {
    this.requestData();
    const alert = this.$route.params.alert;
    if (alert !== undefined) {
      showAlert(this, this.$t(alert), 'success');
    }
  },
  watch: {
    getLicenses: {
      handler: function () {
        this.$refs.licenseTable.refresh();
      }
    }
  },
  computed: {
    getBreadcrumb () {
      return [
        {
          name: this.$t('menu.workspace'),
          link: '/workspace'
        },
        {
          name: this.$t('menu.licenseManagement'),
          link: '/licenseManagement',
          disabled: true
        }
      ];
    },
    getLicenses () {
      return this.licenses;
    }
  },
  methods: {
    fetchData ({page, filter, sort}) {
      page = page === null || isNaN(page) ? 1 : page;
      const tablePage = pagination.createPage(
        this.getLicenses,
        this.tableState,
        ['email', 'license', 'orderId'],
        this.tablePageSize,
        {page, filter, sort});
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    timeFormatter (timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-GB');
    },
    onDelete (license) {
      this.licenseToDelete = license;
      this.deleteModalMessage = this.$t('health.license.deleteModalMessage');
      this.showConfirmationDeleteModal = true;
    },
    onDeleteConfirm () {
      const id = this.licenseToDelete.id;
      this.licenseToDelete = {};
      this.showConfirmationDeleteModal = false;
      licenseApi.deleteLicense(this, id).then(() => {
        showAlert(this, this.$t('health.license.successDelete'), 'success');
        this.requestData();
      }, () => {
        showAlert(this, this.$t('health.license.errorDelete'), 'error');
      });
    },
    onDeleteCancel () {
      this.licenseToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    requestData () {
      licenseApi.getUnusedLicenses(this).then((data) => {
        this.licenses = data;
      });
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};
</script>

<style lang="scss">
  @import "src/stylesheets/variables";

  #header {
    display: inline-block;
    width: 100%;
    line-height: 1rem;
  }
  .header-title {
    float: left;
  }
  .header-add-button {
    float: right;
  }
  .header-add-button-icon {
    font-size: 3rem;
    color: $secondary;
  }
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/LicenseManagement.vue